import { always, identity } from '@kofno/piper';
import { array, Decoder, fail, field, nullable, string, succeed } from 'jsonous';
import { just, Maybe, nothing } from 'maybeasy';
import { NonEmptyList } from 'nonempty-list';
import { err, ok, Result } from 'resulty';

export const assertNever = (x: never) => {
  throw new Error(`Unexpected object: ${x}`);
};

export const stringLiteral = <T extends string>(lit: T): Decoder<T> =>
  string.andThen((s) =>
    s === lit ? succeed<T>(lit) : fail(`Expected the string to be '${lit}' but was '${s}'`),
  );

export const combineDecoders = <A extends object, B extends object>(
  aDecoder: Decoder<A>,
  bDecoder: Decoder<B>,
): Decoder<A & B> =>
  succeed({})
    .assign('a', aDecoder)
    .assign('b', bDecoder)
    .map(({ a, b }) => ({ ...a, ...b }));

export type MissingFieldError = { kind: 'missing-field-error'; name: string };

export const missingFieldError = (name: string): MissingFieldError => ({
  kind: 'missing-field-error',
  name,
});

export const fieldPresenceDecoder = (name: string): Decoder<string> => field(name, succeed(name));

export const optionalField = <T>(name: string, fieldDecoder: Decoder<T>): Decoder<Maybe<T>> =>
  new Decoder((value) =>
    fieldPresenceDecoder(name)
      .decodeAny(value)
      .cata<Result<string, Maybe<T>>>({
        Err: () => ok(nothing()),
        Ok: () => field(name, fieldDecoder).decodeAny(value).map(just),
      }),
  );

export const nullableField = <T>(name: string, decoder: Decoder<T>): Decoder<Maybe<T>> =>
  optionalField(name, nullable(decoder)).map((m) => m.andThen(identity));

export const optionalFieldR = <T>(
  name: string,
  fieldDecoder: Decoder<T>,
): Decoder<Result<MissingFieldError, T>> =>
  optionalField(name, fieldDecoder).map((m) =>
    m.map<Result<MissingFieldError, T>>(ok).getOrElse(always(err(missingFieldError(name)))),
  );

export const maybeInstanceDecoder = <T>(valueDecoder: Decoder<T>): Decoder<Maybe<T>> =>
  new Decoder((u: unknown): Result<string, Maybe<T>> => {
    if (u instanceof Maybe) {
      return u
        .map(valueDecoder.decodeAny)
        .map((rt) => rt.map(just))
        .getOrElse(() => ok(nothing()));
    }
    return err(`Expected a Maybe instance, but got ${JSON.stringify(u)}`);
  });

export const nonEmptyListInstanceDecoder = <T>(
  valueDecoder: Decoder<T>,
): Decoder<NonEmptyList<T>> =>
  new Decoder((u: unknown): Result<string, NonEmptyList<T>> => {
    if (u instanceof NonEmptyList) {
      return valueDecoder
        .decodeAny(u.first)
        .map((t) => new NonEmptyList(t, []))
        .andThen((l) => array(valueDecoder).decodeAny(u.rest).map(l.concat));
    }
    return err(`Expected a NonEmptyList instance, but got ${JSON.stringify(u)}`);
  });

type SnakeToDashCase<S extends string> = S extends `${infer A}_${infer B}`
  ? `${SnakeToDashCase<A>}-${SnakeToDashCase<B>}`
  : S;

const snakeToDashCase = <S extends string>(s: S): SnakeToDashCase<S> =>
  s.replace(/_/g, '-') as SnakeToDashCase<S>;

export const snakeLiteralToDashCaseDecoder = <S extends string>(
  snake: S,
): Decoder<SnakeToDashCase<S>> => stringLiteral(snake).map(snakeToDashCase);

export const readonlyArray = <T>(decoder: Decoder<T>): Decoder<ReadonlyArray<T>> =>
  array(decoder).map<ReadonlyArray<T>>(identity);

export const nonEmptyList = <T>(decoder: Decoder<T>): Decoder<NonEmptyList<T>> =>
  array(decoder).andThen((a) =>
    a.length > 0 ? succeed(new NonEmptyList(a[0], a.slice(1))) : fail('Expected a non-empty list'),
  );
